import { AdobeAppEventDataSdk } from '@sxm-core/utility-code/typescript/browser-adobe-app-event-data';

export class EventTrackingService {
    private readonly adobeAppEventDataSdk = new AdobeAppEventDataSdk();

    flowStartedTrack(flowInfo: { [key: string]: unknown }) {
        this.eventTrack('flow-started', { flowInfo });
    }

    offersPresentedTracking(offerInfo: { [key: string]: unknown }) {
        this.eventTrack('offers-presented', { offerInfo });
    }

    offersSelectedTracking(selectedOfferInfo: Record<string, unknown>) {
        this.eventTrack('offers-selected', { selectedOfferInfo });
    }

    transactionIdUpdateTrack(transactionId: string) {
        this.eventTrack('transaction-id-update', {
            transactionInfo: { transactionId },
        });
    }

    customerInfoTrack(customerInfo: { [key: string]: unknown }) {
        this.eventTrack('customer-info', { customerInfo });
    }

    purchaseTrack(purchaseInfo: { [key: string]: unknown }) {
        this.eventTrack('purchase', { purchaseInfo });
    }

    cancelOnlineStartEvent(cancelOnlineEligibility: Record<string, unknown>) {
        this.eventTrack('cancel-online-start', { cancelOnlineEligibility });
    }

    pageTrack(pageInfo: { flowName: string; componentName: string }): void {
        this.eventTrack('page-loaded', { pageInfo: pageInfo });
    }

    passkeyTrack(passkeyInfo: Record<string, unknown>): void {
        this.eventTrack('passkey-event', { passkeyInfo });
    }

    componentTrack(
        componentInfo: { componentName: string },
        eventName = 'component-loaded',
    ): void {
        this.eventTrack(eventName, { componentInfo: componentInfo });
    }

    clickTrack(clickInfo: {
        linkName: string;
        linkType: string;
        linkKey?: string;
    }): void {
        this.eventTrack('user-click', { clickInfo: clickInfo });
    }

    deviceInfoTracking(
        deviceInfo: { [key: string]: unknown },
        eventName = 'device-info',
    ) {
        this.eventTrack(eventName, { deviceInfo });
    }

    signinVerificationMethodsTrack(flowInfo: { [key: string]: unknown }) {
        this.eventTrack('signin-verification-methods', {
            flowInfo,
        });
    }

    signInSuccessTracking(customerInfo: { [key: string]: unknown }) {
        this.eventTrack('signin-success', { customerInfo });
    }

    signinFailureTracking(customerInfo: { [key: string]: unknown }) {
        this.eventTrack('signin-failure', { customerInfo });
    }

    eddlUpdateTracking(updates: { [key: string]: unknown }) {
        this.eventTrack('eddl-update', updates);
    }

    frontEndErrorsTrack(
        frontEndErrors: string[],
        eventName = 'user-error',
    ): void {
        const errors = frontEndErrors.map<{
            readonly errorType: 'USER';
            errorName: string;
        }>((errorName) => ({ errorType: 'USER', errorName }));
        this.eventTrack(eventName, { errors });
    }

    businessErrorTrack(businessErrorInfo: {
        readonly errorType: 'BUSINESS';
        errorName: string;
        errorCode: string;
    }): void {
        this.eventTrack('business-error', { errors: [businessErrorInfo] });
    }

    appErrorsTrack(errors: string[]): void {
        const errorInfo = errors.map<{
            readonly errorType: 'APP';
            errorName: string;
        }>((errorName) => ({ errorType: 'APP', errorName }));
        this.eventTrack('app-error', { errors: errorInfo });
    }

    systemErrorTrack(error: {
        readonly errorType: 'SYSTEM';
        errorName: string;
        errorCode: string;
    }): void {
        this.eventTrack('system-error', { errors: [error] });
    }

    cancelReasonTrack(planInfo: Record<string, string>) {
        this.eventTrack('cancel-reason-info', { planInfo });
    }

    giftCardAppliedTracking(giftCardApplied: boolean) {
        this.eventTrack('gift-card-applied', {
            giftCardInfo: { giftCardApplied },
        });
    }

    private pushEvent(event: { event: string; [key: string]: unknown }): void {
        this.adobeAppEventDataSdk.trackEvent(event);
    }

    private eventTrack(event: string, data?: { [key: string]: unknown }): void {
        this.pushEvent({ event, ...data });
    }
}
